import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Gift, Zap, Star, CheckCircle, Check, AlertTriangle } from 'lucide-react';
import { useSubscription } from '@/context/SubscriptionContext';
import { PLANS } from '@/config/subscriptionPlans';
import { launchPurchaseFlow, BILLING_PRODUCT_IDS, BILLING_OFFER_ID_PRO_TRIAL, BILLING_OFFER_ID_PERSONAL_TRIAL, BILLING_UNAVAILABLE_MESSAGE } from '@/services/billingService';
import { formatTrialExpiresAt } from '@/services/trialService';
import { Capacitor } from '@capacitor/core';

export function PlanUpgradeModal({ isOpen, onClose }) {
  const {
    plan, setPlan, isFree, isPremiumPersonal, isPremiumPro,
    cachedProducts, restorePurchases, billingAvailable,
    trialPlan, trialExpiresAt, trialDaysRemaining,
  } = useSubscription();
  const [purchasing, setPurchasing] = useState(false);

  // Billing no disponible en Android nativo cuando billingAvailable === false
  const isAndroidNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  const showBillingUnavailable = isAndroidNative && billingAvailable === false;

  // Trial activo: hay trial Pro vigente
  const isOnTrial = trialPlan === PLANS.PREMIUM_PRO;
  // Premium Pro real (por Billing o internalEntitlement, NO por trial)
  const isPremiumProReal = isPremiumPro && !isOnTrial;

  // ── Helper: obtiene el offerToken correcto del caché de productos ──
  const getOfferToken = (productId) => {
    const trialOfferId =
      productId === BILLING_PRODUCT_IDS.PREMIUM_PRO      ? BILLING_OFFER_ID_PRO_TRIAL :
      productId === BILLING_PRODUCT_IDS.PREMIUM_PERSONAL ? BILLING_OFFER_ID_PERSONAL_TRIAL :
      null;

    if (trialOfferId) {
      const trialOffer = cachedProducts.find(
        p => p.productId === productId && p.offerId === trialOfferId
      );
      if (trialOffer) return trialOffer.offerToken;
    }

    const baseOffer = cachedProducts.find(
      p => p.productId === productId && p.offerId === ''
    );
    return baseOffer?.offerToken ?? null;
  };

  // ── Lanzar compra real o fallback en web/dev ──
  const handleSelectPlan = async (productId) => {
    if (!isAndroidNative) {
      setPlan(productId);
      onClose();
      return;
    }

    const offerToken = getOfferToken(productId);
    if (!offerToken) {
      console.error('[BILLING] No offerToken found for', productId);
      return;
    }

    setPurchasing(true);
    try {
      await launchPurchaseFlow(productId, offerToken);
      // El plan se actualizará automáticamente vía listener en SubscriptionContext
      onClose();
    } catch (err) {
      console.error('[BILLING] Purchase error:', err);
    } finally {
      setPurchasing(false);
    }
  };

  // ── Textos de botones ──
  const personalButtonLabel = isPremiumPersonal
    ? 'Tu plan actual'
    : purchasing ? 'Procesando…' : 'Activar Premium Personal';

  const proButtonLabel =
    isOnTrial          ? 'Tu prueba actual' :
    isPremiumProReal   ? 'Tu plan actual'   :
    purchasing         ? 'Procesando…'      :
    'Activar Premium Pro';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#121212] text-white border-[#2A2A2A]">
        <DialogHeader>
          <DialogTitle className="text-xl text-white">Elige tu plan</DialogTitle>
          <p className="text-sm text-[#909090] mt-1">
            Disfruta Rutapp Pro gratis durante 60 días. Sin tarjeta y sin compromiso.
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">

          {/* ── TARJETA DE ESTADO ACTUAL ── */}

          {/* Estado: Trial Pro activo */}
          {isOnTrial && (
            <div className="flex items-start gap-3 rounded-xl border border-blue-500/40 bg-blue-500/10 p-4">
              <Gift className="h-5 w-5 mt-0.5 shrink-0 text-blue-400" />
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-blue-200 text-sm">
                  Estás usando Rutapp Pro gratis
                </span>
                <span className="text-blue-300 text-sm">
                  {trialDaysRemaining > 1
                    ? `Te quedan ${trialDaysRemaining} días de prueba.`
                    : trialDaysRemaining === 1
                      ? 'Te queda 1 día de prueba.'
                      : 'Tu prueba expira hoy.'}
                </span>
                {trialExpiresAt && (
                  <span className="text-blue-400 text-xs">
                    Finaliza el {formatTrialExpiresAt(trialExpiresAt)}
                  </span>
                )}
                <span className="text-[#7090B0] text-xs mt-0.5">
                  Sin tarjeta. Sin cobro automático.
                </span>
                <span className="text-[#5878A0] text-xs">
                  Al terminar podrás seguir en Free o activar Premium.
                </span>
              </div>
            </div>
          )}

          {/* Estado: Premium Pro real (Billing o internalEntitlement) */}
          {isPremiumProReal && (
            <div className="flex items-center gap-3 rounded-xl border border-blue-500/40 bg-blue-500/10 p-4">
              <Zap className="h-5 w-5 shrink-0 text-blue-400" />
              <span className="font-semibold text-blue-200 text-sm">Premium Pro activo</span>
            </div>
          )}

          {/* Estado: Premium Personal */}
          {isPremiumPersonal && (
            <div className="flex items-center gap-3 rounded-xl border border-yellow-500/40 bg-yellow-500/10 p-4">
              <Star className="h-5 w-5 shrink-0 text-yellow-400" />
              <span className="font-semibold text-yellow-200 text-sm">Premium Personal activo</span>
            </div>
          )}

          {/* Estado: Free */}
          {isFree && (
            <div className="flex items-center gap-3 rounded-xl border border-[#2A2A2A] bg-[#1A1A1A] p-4">
              <CheckCircle className="h-5 w-5 shrink-0 text-[#505050]" />
              <span className="font-semibold text-[#808080] text-sm">Estás en modo Free</span>
            </div>
          )}

          {/* ── Aviso de Billing no disponible ── */}
          {showBillingUnavailable && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-400" />
              <span>{BILLING_UNAVAILABLE_MESSAGE}</span>
            </div>
          )}

          {/* ── TARJETA FREE ── */}
          <div className={`rounded-xl border p-4 space-y-3 ${
            isFree && !isOnTrial
              ? 'border-[#404040] bg-[#1E1E1E]'
              : 'border-[#1E1E1E] bg-[#141414]'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-[#505050]" />
                  <h3 className="font-semibold text-base text-white">Free</h3>
                </div>
                <p className="text-lg font-bold text-white mt-1">Gratis</p>
              </div>
              {isFree && !isOnTrial && (
                <span className="text-xs bg-[#2A2A2A] text-[#909090] rounded-full px-2.5 py-1 font-medium">
                  Plan actual
                </span>
              )}
            </div>
            <ul className="space-y-2 text-sm text-[#808080]">
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-[#505050] shrink-0" />1 perfil
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-[#505050] shrink-0" />Hasta 3 clientes por día
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-[#505050] shrink-0" />Google Maps
              </li>
              <li className="flex items-center gap-2 opacity-35">
                <span className="h-3.5 w-3.5 shrink-0 text-center leading-none text-xs">✕</span>
                <span className="line-through">Funciones avanzadas</span>
              </li>
            </ul>
            <Button
              variant="outline"
              className="w-full border-[#2A2A2A] text-[#707070] bg-transparent cursor-default"
              disabled
            >
              {isFree && !isOnTrial ? 'Plan actual' : 'Plan Free'}
            </Button>
          </div>

          {/* ── TARJETA PREMIUM PERSONAL ── */}
          <div className={`rounded-xl border p-4 space-y-3 ${
            isPremiumPersonal
              ? 'border-yellow-500/50 bg-yellow-500/5'
              : 'border-[#2A2A2A] bg-[#161616]'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-500" />
                  <h3 className="font-semibold text-base text-white">Premium Personal</h3>
                </div>
                <p className="text-lg font-bold text-white mt-1">5,99 €/mes</p>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                {isPremiumPersonal && (
                  <span className="text-xs bg-yellow-500/20 text-yellow-300 rounded-full px-2.5 py-1 font-medium">
                    Tu plan actual
                  </span>
                )}
                <span className="text-xs bg-[#1E1E1E] text-[#707070] rounded-full border border-[#2A2A2A] px-2.5 py-1">
                  Plan individual
                </span>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-[#B0B0B0]">
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />1 perfil
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />Clientes ilimitados
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />Nota informativa, web e imagen rápida
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />WhatsApp rápido con clientes
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />Estados de ruta
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />Nombre de empresa editable
              </li>
              <li className="flex items-center gap-2 opacity-40">
                <span className="h-3.5 w-3.5 shrink-0 text-center leading-none text-xs">✕</span>
                <span className="line-through">Funciones entre equipos</span>
              </li>
            </ul>
            <Button
              className="w-full bg-yellow-600 hover:bg-yellow-500 text-white"
              disabled={isPremiumPersonal || purchasing || showBillingUnavailable}
              onClick={() => handleSelectPlan(PLANS.PREMIUM_PERSONAL)}
            >
              {personalButtonLabel}
            </Button>
          </div>

          {/* ── TARJETA PREMIUM PRO (destacada) ── */}
          <div className={`rounded-xl border-2 p-4 space-y-3 ${
            isPremiumPro
              ? 'border-blue-500 bg-blue-500/5'
              : 'border-blue-500/40 bg-[#13151F]'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-blue-400" />
                  <h3 className="font-semibold text-base text-white">Premium Pro</h3>
                </div>
                <p className="text-lg font-bold text-white mt-1">11,99 €/mes</p>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                {isOnTrial && (
                  <span className="text-xs bg-blue-500/20 text-blue-300 rounded-full px-2.5 py-1 font-medium">
                    Tu prueba actual
                  </span>
                )}
                {isPremiumProReal && (
                  <span className="text-xs bg-blue-500/20 text-blue-300 rounded-full px-2.5 py-1 font-medium">
                    Tu plan actual
                  </span>
                )}
                <span className="text-xs bg-blue-600/25 text-blue-300 rounded-full border border-blue-500/30 px-2.5 py-1 font-medium">
                  Recomendado
                </span>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-[#C0C8D8]">
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-blue-400 shrink-0" />Varios perfiles
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-blue-400 shrink-0" />Clientes ilimitados
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-blue-400 shrink-0" />Todo lo de Premium Personal
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-blue-400 shrink-0" />Alertas entre perfiles
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-blue-400 shrink-0" />WhatsApp entre equipos
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-blue-400 shrink-0" />Estados compartidos entre perfiles
              </li>
            </ul>
            <Button
              className={`w-full text-white font-medium ${
                isPremiumPro
                  ? 'bg-blue-700/50 cursor-default'
                  : 'bg-blue-600 hover:bg-blue-500'
              }`}
              disabled={isPremiumPro || showBillingUnavailable || purchasing}
              onClick={() => handleSelectPlan(PLANS.PREMIUM_PRO)}
            >
              {proButtonLabel}
            </Button>
          </div>

          {/* ── Restaurar / Gestionar ── */}
          <div className="pt-2 space-y-1 border-t border-[#1E1E1E]">
            <Button
              variant="ghost"
              className="w-full text-[#606060] hover:text-white hover:bg-[#1E1E1E] text-sm"
              onClick={restorePurchases}
              disabled={purchasing}
            >
              Restaurar compra
            </Button>
            <Button
              variant="ghost"
              className="w-full text-[#606060] hover:text-white hover:bg-[#1E1E1E] text-sm"
              onClick={() => {
                window.open(
                  'https://play.google.com/store/account/subscriptions?package=com.rutapp.app',
                  '_system'
                );
              }}
            >
              Gestionar suscripción
            </Button>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}
